import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Only load the current language at startup to reduce bundle size
// Other language loaded on-demand if user switches
const NAMESPACES = [
  'common',
  'catalog',
  'detail',
  'wizard',
  'login',
  'cookbooks',
  'planner',
  'shoppingList',
  'username',
  'sharing',
  'cookbookSharing',
  'settings',
  'ingredients',
  'feedback',
  'consent',
  'cookies',
  'pageTitles',
  'notFound',
  'recipeImport',
  'groups',
  'pwa',
  'macros',
  'challenges',
];

// Lazy-load translation files for a specific language
export async function loadLanguageResources(lng: string) {
  const resources: Record<string, Record<string, object>> = { [lng]: {} };

  for (const ns of NAMESPACES) {
    try {
      // Dynamic import: each namespace file is loaded only when needed
      const module = await import(`./locales/${lng}/${ns}.json`);
      resources[lng][ns] = module.default;
    } catch (error) {
      console.warn(`Failed to load namespace "${ns}" for language "${lng}"`, error);
    }
  }

  return resources;
}

// Initialize with minimal config — resources added before first render via initI18n()
i18n.use(initReactI18next).init({
  lng: 'es',
  fallbackLng: 'en',
  defaultNS: 'common',
  ns: NAMESPACES,
  interpolation: {
    escapeValue: false,
  },
  resources: {},
});

// Call this before mounting React to ensure no flash of untranslated keys
export async function initI18n(): Promise<void> {
  const resources = await loadLanguageResources('es');
  Object.entries(resources.es).forEach(([ns, content]) => {
    i18n.addResourceBundle('es', ns, content, true, true);
  });
}

// Add method to dynamically load another language (e.g., when user switches)
export async function loadLanguageAsync(lng: string): Promise<void> {
  if (i18n.hasResourceBundle(lng, i18n.options.defaultNS as string)) {
    return;
  }

  const resources = await loadLanguageResources(lng);
  Object.entries(resources[lng]).forEach(([ns, content]) => {
    i18n.addResourceBundle(lng, ns, content, true, true);
  });
}

export default i18n;
